import { CARD_COLOUR_LABELS, CARD_PRIORITY_LABELS, ITEM_TYPE_LABELS, MOSCOW_LABELS, STATUS_LABELS } from './constants.js'
import {
  RELATIONSHIP_LABELS,
  contextOptionLabel,
  describeSource,
  describeSourceMapping,
  relevantTypedSections,
  scalarField,
  typedFieldLabel,
  typedSection,
  type CollectionKey,
  type EffectiveContext,
  type RelatedComponent,
  type ResolvedValue,
  type ScalarKey,
} from './context.js'
import {
  INTENT_LIMITS,
  isLinkableLocator,
  requirementGroupLabel,
  type CardIntent,
} from './intent.js'
import type { CardDraft } from './schemas.js'

/**
 * Assembles a Markdown implementation prompt from card values the user wrote.
 *
 * Pure and deterministic: same input, same output. It never generates requirements,
 * never rewrites or reconciles what the user supplied, and never truncates — content
 * over the export bound is reported so the user can shorten it themselves.
 */

/** Bumped whenever the assembled document's shape changes. */
export const PROMPT_TEMPLATE_VERSION = 2

export interface PromptLocation {
  boardName: string
  columnGroup: string | null
  column: string
  swimlane: string
  row: string
}

export interface PromptInput extends CardDraft {
  location?: PromptLocation | undefined
  /** Resolved architecture context for the chosen scopes. Kept structured until here. */
  effectiveContext?: EffectiveContext | undefined
  /** Typed relationships touching the card, in both directions. */
  related?: readonly RelatedComponent[] | undefined
  /** Say where inherited values came from. Defaults to true; concise mode turns it off. */
  provenance?: boolean | undefined
}

export interface PromptSection {
  key: string
  heading: string
  characters: number
}

export interface PromptResult {
  templateVersion: number
  markdown: string
  characterCount: number
  sections: PromptSection[]
  /** Non-blocking hints about missing information. Never a readiness score. */
  advisories: string[]
  overLimit: boolean
  exportLimit: number
  /** Largest sections first, so a user over the limit knows what to shorten. */
  largestSections: PromptSection[]
}

const has = (value: string): boolean => value.trim().length > 0

/** Quoted source material is blockquoted so it stays distinct from task instructions. */
function quote(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => (line.length > 0 ? `> ${line}` : '>'))
    .join('\n')
}

class Builder {
  private readonly parts: string[] = []
  readonly sections: PromptSection[] = []

  section(key: string, heading: string, body: string): void {
    const trimmed = body.replace(/\s+$/, '')
    if (trimmed.length === 0) return
    const block = heading.length > 0 ? `## ${heading}\n${trimmed}` : trimmed
    this.parts.push(block)
    this.sections.push({ key, heading, characters: block.length })
  }

  raw(key: string, heading: string, block: string): void {
    this.parts.push(block)
    this.sections.push({ key, heading, characters: block.length })
  }

  toMarkdown(): string {
    return `${this.parts.join('\n\n')}\n`
  }
}

function planningLines(input: PromptInput, intent: CardIntent): string[] {
  const p = intent.planning
  const lines: string[] = []
  if (p.type && input.type) lines.push(`- Type: ${ITEM_TYPE_LABELS[input.type]}`)
  if (p.status) lines.push(`- Status: ${STATUS_LABELS[input.status]}`)
  if (p.priority && input.priority) lines.push(`- Priority: ${CARD_PRIORITY_LABELS[input.priority]}`)
  if (p.moscow && input.moscow) lines.push(`- MoSCoW: ${MOSCOW_LABELS[input.moscow]}`)
  if (p.owner && has(input.owner)) lines.push(`- Owner: ${input.owner.trim()}`)
  if (p.release && has(input.release)) lines.push(`- Release: ${input.release.trim()}`)
  if (p.tags && input.tags.length > 0) lines.push(`- Tags: ${input.tags.join(', ')}`)
  if (p.dueDate && input.dueDate) lines.push(`- Due date: ${input.dueDate}`)
  if (p.estimateMinutes && input.estimateMinutes !== null) {
    lines.push(`- Estimate: ${input.estimateMinutes} minutes`)
  }
  if (p.colour) lines.push(`- Card colour: ${CARD_COLOUR_LABELS[input.colour]}`)
  if (p.location && input.location) {
    const { boardName, columnGroup, column, swimlane, row } = input.location
    const columnLabel = columnGroup ? `${columnGroup} / ${column}` : column
    lines.push(`- Board location: ${boardName} — column ${columnLabel}, row ${swimlane} / ${row}`)
  }
  return lines
}

/** Each architecture section, in the order the prompt presents them. */
const ARCHITECTURE_SECTIONS: readonly { key: string; heading: string; scalars: ScalarKey[]; collection?: CollectionKey }[] = [
  { key: 'project', heading: 'Project context', scalars: ['application', 'runtime'] },
  { key: 'architecture', heading: 'Architectural context', scalars: ['architectureLayer', 'componentType'] },
  { key: 'domain', heading: 'Domain context', scalars: ['domain'] },
  { key: 'responsibility', heading: 'Component responsibility', scalars: ['responsibility'] },
  { key: 'technologies', heading: 'Technology context', scalars: [], collection: 'technologies' },
  { key: 'dependencies', heading: 'Dependencies', scalars: [], collection: 'dependencies' },
  { key: 'interfaces', heading: 'Interfaces', scalars: [], collection: 'interfaces' },
  { key: 'dataOwnership', heading: 'Data ownership', scalars: [], collection: 'dataOwnership' },
  { key: 'security', heading: 'Security constraints', scalars: ['securityBoundary'] },
  { key: 'constraints', heading: 'Architectural constraints', scalars: [], collection: 'constraints' },
  { key: 'nonFunctional', heading: 'Non-functional requirements', scalars: [], collection: 'nonFunctional' },
  { key: 'operational', heading: 'Operational considerations', scalars: [], collection: 'operational' },
  { key: 'conventions', heading: 'Engineering conventions', scalars: [], collection: 'conventions' },
]

function architectureSections(builder: Builder, context: EffectiveContext, provenance: boolean): void {
  const from = (resolved: { inherited: boolean; source: Parameters<typeof describeSource>[0] }) =>
    provenance && resolved.inherited ? ` (inherited from ${describeSource(resolved.source)})` : ''
  const scalarLine = (label: string, resolved: ResolvedValue, display: string) =>
    `- ${label}: ${display}${from(resolved)}`

  for (const section of ARCHITECTURE_SECTIONS) {
    const lines: string[] = []
    for (const key of section.scalars) {
      const resolved = context.scalars[key]
      if (!resolved) continue
      const field = scalarField(key)
      lines.push(scalarLine(field.label, resolved, field.options ? contextOptionLabel(resolved.value) : resolved.value))
    }
    if (section.collection) {
      for (const item of context.collections[section.collection]) lines.push(`- ${item.value}${from(item)}`)
    }
    // Type-specific context belongs with the technology it describes.
    if (section.key === 'technologies') {
      for (const sectionKey of relevantTypedSections(context)) {
        const fields = context.typed[sectionKey]
        if (!fields || Object.keys(fields).length === 0) continue
        const label = typedSection(sectionKey)?.label ?? sectionKey
        if (lines.length > 0) lines.push('')
        lines.push(`${label}:`)
        const ordered = [
          ...(typedSection(sectionKey)?.fields.map((field) => field.key) ?? []),
          ...Object.keys(fields),
        ].filter((key, index, all) => fields[key] && all.indexOf(key) === index)
        for (const fieldKey of ordered) {
          lines.push(scalarLine(typedFieldLabel(sectionKey, fieldKey), fields[fieldKey]!, fields[fieldKey]!.value))
        }
      }
    }
    builder.section(`context:${section.key}`, section.heading, lines.join('\n'))
  }
}

function relatedLines(related: readonly RelatedComponent[]): string[] {
  return related.map((item) => {
    const kind = item.otherComponentType ? ` (${contextOptionLabel(item.otherComponentType)})` : ''
    const other = `"${item.otherTitle}"${kind}`
    const verb = RELATIONSHIP_LABELS[item.relationship.type].toLowerCase()
    const head =
      item.direction === 'outgoing' ? `- This component ${verb} ${other}` : `- ${other} ${verb} this component`
    const details = [
      item.relationship.mode ? (item.relationship.mode === 'sync' ? 'synchronous' : 'asynchronous') : '',
      item.relationship.protocol ? `protocol: ${item.relationship.protocol}` : '',
      item.relationship.contract ? `contract: ${item.relationship.contract}` : '',
    ].filter((value) => value.length > 0)
    const suffix = details.length > 0 ? ` [${details.join('; ')}]` : ''
    const description = item.relationship.description ? ` — ${item.relationship.description}` : ''
    const responsibility = item.otherResponsibility ? `\n  - Its responsibility: ${item.otherResponsibility}` : ''
    return `${head}${suffix}${description}${responsibility}`
  })
}

export function buildPrompt(input: PromptInput): PromptResult {
  const intent = input.intent
  const builder = new Builder()
  const title = input.title.trim() || 'Untitled card'

  // 1. Title and user outcome.
  builder.raw('title', title, `# Implement: ${title}`)

  // Architecture context, when any scope supplied it. Absent context leaves the prompt as it was.
  const provenance = input.provenance ?? true
  if (input.effectiveContext) architectureSections(builder, input.effectiveContext, provenance)
  if (input.related && input.related.length > 0) {
    builder.section('context:related', 'Related components', relatedLines(input.related).join('\n'))
  }
  if (input.effectiveContext) {
    const mappings = input.effectiveContext.sourceMappings
    builder.section(
      'context:sourceMappings',
      'Source mappings',
      mappings
        .map(
          (item) =>
            `- ${describeSourceMapping(item.value)}${provenance && item.inherited ? ` (inherited from ${describeSource(item.source)})` : ''}`,
        )
        .join('\n'),
    )
  }

  builder.section('userOutcome', 'User outcome', intent.userOutcome)

  // 2. Description and scope boundaries.
  builder.section('description', 'Description', input.description)
  builder.section('inScope', 'Scope', intent.inScope)
  builder.section('outOfScope', 'Out of scope', intent.outOfScope)

  // 3. Acceptance criteria.
  const criteria = intent.acceptanceCriteria.filter((criterion) => has(criterion.text))
  if (criteria.length > 0) {
    builder.section(
      'acceptanceCriteria',
      'Acceptance criteria',
      criteria.map((criterion) => `- ${criterion.text.trim()}`).join('\n'),
    )
  }

  // 4. Constraints, notes and every included requirement entry, headings preserved.
  builder.section('constraints', 'Constraints', intent.constraints)
  builder.section('notes', 'Technical and UX notes', intent.notes)
  for (const entry of intent.requirements) {
    if (!entry.include) continue
    if (!has(entry.heading) && !has(entry.body)) continue
    const groupLabel = requirementGroupLabel(entry.group)
    const heading = has(entry.heading) ? `${groupLabel} — ${entry.heading.trim()}` : groupLabel
    builder.section(`requirement:${entry.id}`, heading, entry.body)
  }

  // 5. References. A locator is a pointer; only a pasted excerpt is actually carried.
  for (const reference of intent.references) {
    if (!reference.include) continue
    const label = has(reference.label) ? reference.label.trim() : 'Reference'
    if (!has(reference.locator) && !has(reference.relevance) && !has(reference.excerpt)) continue
    const lines: string[] = [`## Reference — ${label}`]
    if (has(reference.locator)) {
      const locator = reference.locator.trim()
      lines.push(`Location: ${locator}${isLinkableLocator(locator) ? '' : ' (path, not a URL)'}`)
    }
    lines.push(
      has(reference.excerpt)
        ? 'An excerpt supplied by the user is quoted below.'
        : 'Reference only; its contents are not included in this prompt.',
    )
    if (has(reference.relevance)) lines.push(`Relevance: ${reference.relevance.trim()}`)
    if (has(reference.excerpt)) lines.push('', quote(reference.excerpt.replace(/\s+$/, '')))
    builder.raw(`reference:${reference.id}`, `Reference — ${label}`, lines.join('\n'))
  }

  // 6. Verification.
  builder.section('verification', 'Verification notes', intent.verification)

  // Planning values, recorded literally and labelled as planning rather than requirements.
  const planning = planningLines(input, intent)
  if (planning.length > 0) {
    builder.section(
      'planning',
      'Card planning context',
      [
        'Planning values recorded on the board, for background only. They are not requirements:',
        'a status is not an instruction, an estimate is not a performance target, and a card',
        'colour is not a requested UI colour.',
        '',
        ...planning,
      ].join('\n'),
    )
  }

  // 7. Open questions.
  builder.section('openQuestions', 'Open questions', intent.openQuestions)

  // 7 and 8. Working instructions and the requested completion report.
  builder.section(
    'instructions',
    'Working instructions',
    [
      'Inspect the available project context and identify missing information.',
      'Surface consequential open questions before making assumptions that depend on them.',
      'Do not invent requirements, acceptance criteria or architectural facts to fill gaps.',
      'Report the changes made, the checks performed and any unresolved issues.',
    ].join('\n'),
  )

  const markdown = builder.toMarkdown()
  const characterCount = markdown.length

  const advisories: string[] = []
  if (criteria.length === 0) advisories.push('Consider adding acceptance criteria.')
  if (!has(intent.userOutcome)) advisories.push('Consider adding the intended user outcome.')
  if (!has(input.description) && !has(intent.inScope)) {
    advisories.push('Consider describing the work, or what is in scope.')
  }

  const largestSections = [...builder.sections].sort((a, b) => b.characters - a.characters).slice(0, 5)

  return {
    templateVersion: PROMPT_TEMPLATE_VERSION,
    markdown,
    characterCount,
    sections: builder.sections,
    advisories,
    overLimit: characterCount > INTENT_LIMITS.exportCharacters,
    exportLimit: INTENT_LIMITS.exportCharacters,
    largestSections,
  }
}

/** Safe filename for the Markdown download; unsaved new cards get a fixed draft name. */
export function promptFilename(cardId: string | null): string {
  if (!cardId) return 'card-prompt-draft.md'
  const safe = cardId.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 36) || 'card'
  return `card-prompt-${safe}.md`
}
